import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Upload, Loader2, X, Truck, CheckCircle2, Package } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import Logo from "../components/Logo";
import SEO from "../components/SEO";
import { Stars } from "../components/Reviews";
import { useApp } from "../context/AppContext";
import { api, chf, fileUrl, imgSrc, fontLabel, formatApiErrorDetail } from "../lib/api";

const EMPTY = { name: "", category: "Mug", description: "", price: "", image_url: "", images: [], discount_percent: 0, customizable: true, active: true, sizes_input: "", size_label: "Taille", in_realisations: false, technique: "Sublimation", featured_home: false, weight_grams: 0, wrap_canvas: false, shipping_format: "enveloppe", thickness_mm: 0, material: "", dimensions: "", production_time: "", care_instructions: "" };
const CATS = ["Mug", "Gourde", "Tirelire", "Boîte", "Planche", "Sous-plat", "Sous-verre", "Carnet", "Stylo", "Porte-clés", "Médaille", "Tote bag", "Sac", "Bois", "Métal", "Autre"];
// Libellé de variante par défaut selon la catégorie (contenants = volume, supports plats = dimensions)
const SIZE_LABEL_BY_CAT = {
  Mug: "Contenance", Gourde: "Contenance", "Boîte": "Contenance",
  Planche: "Dimension", "Sous-plat": "Dimension", "Sous-verre": "Dimension", Tirelire: "Dimension",
};

// Tableau des produits (utilisé pour les sections Catalogue / Shop / Non publiés)
function ProductsTable({ items, onEdit, onDelete }) {
  return (
    <div className="overflow-x-auto border border-line">
      <table className="w-full text-sm">
        <thead className="bg-sand text-left text-xs uppercase tracking-widest text-ink3">
          <tr>
            <th className="p-4">Produit</th><th className="p-4">Catégorie</th><th className="p-4">Prix</th>
            <th className="p-4">Réduc.</th><th className="p-4">Statut</th><th className="p-4"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((p) => (
            <tr key={p.id} data-testid={`admin-product-${p.id}`} className="hover:bg-sand/40">
              <td className="flex items-center gap-3 p-4">
                <img src={imgSrc(p.image_url)} alt="" className="h-12 w-10 border border-line object-cover" />
                <span className="font-medium text-ink">{p.name}</span>
              </td>
              <td className="p-4 text-ink2">{p.category}</td>
              <td className="p-4 text-ink2">{chf(p.price)}</td>
              <td className="p-4 text-ink2">{p.discount_percent ? `${p.discount_percent}%` : "—"}</td>
              <td className="p-4">{p.active ? <span className="text-sage">Actif</span> : <span className="text-ink3">Masqué</span>}</td>
              <td className="p-4">
                <div className="flex justify-end gap-2">
                  <button onClick={() => onEdit(p)} data-testid={`edit-${p.id}`} className="p-2 text-ink2 hover:text-ink"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => onDelete(p.id)} data-testid={`delete-${p.id}`} className="p-2 text-ink2 hover:text-terra"><Trash2 className="h-4 w-4" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useApp();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [ship, setShip] = useState(null);
  const [form, setForm] = useState(null); // null=closed, obj=editing/new
  const [uploading, setUploading] = useState(false);
  const [dCode, setDCode] = useState("");
  const [dPct, setDPct] = useState(10);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

  const changePassword = async () => {
    if (pw.next.length < 8) { toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères"); return; }
    if (pw.next !== pw.confirm) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Mot de passe mis à jour");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const load = useCallback(async () => {
    const [p, d, o, rv, sh] = await Promise.all([
      api.get("/products?include_inactive=true"),
      api.get("/discounts"),
      api.get("/orders"),
      api.get("/reviews"),
      api.get("/shipping/settings"),
    ]);
    setProducts(p.data); setDiscounts(d.data); setOrders(o.data); setReviews(rv.data); setShip(sh.data);
  }, []);

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (user && user.role !== "admin") { navigate("/login"); return; }
    if (user && user.role === "admin") load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [user, navigate, load]);

  if (!user || user.role !== "admin") {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-terra" /></div>;
  }

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Chemin relatif volontairement : l'URL absolue est résolue à l'affichage (domain-proof)
      setForm((f) => ({ ...f, image_url: `/api/files/${data.path}` }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const uploadExtraImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...(f.images || []), `/api/files/${data.path}`] }));
      toast.success("Image ajoutée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); e.target.value = ""; }
  };

  const saveProduct = async () => {
    if (!form.name || !form.price) { toast.error("Nom et prix requis"); return; }
    const payload = { ...form, price: parseFloat(form.price), discount_percent: parseFloat(form.discount_percent) || 0,
      sizes: (form.sizes_input || "").split(",").map((s) => s.trim()).filter(Boolean),
      size_label: form.size_label || "Taille",
      technique: form.technique || "Sublimation",
      faces: [],
      in_realisations: !!form.in_realisations,
      featured_home: !!form.featured_home,
      weight_grams: parseInt(form.weight_grams, 10) || 0,
      thickness_mm: parseInt(form.thickness_mm, 10) || 0,
      shipping_format: form.shipping_format || "enveloppe" };
    delete payload.sizes_input;
    delete payload.extra_image_url;
    try {
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Produit enregistré");
      setForm(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Produit supprimé");
    load();
  };

  const editProduct = (p) => setForm({
    ...p,
    price: String(p.price),
    sizes_input: (p.sizes || []).join(", "),
    size_label: p.size_label || "Taille",
    in_realisations: !!p.in_realisations,
    technique: p.technique || "Sublimation",
    featured_home: !!p.featured_home,
    weight_grams: p.weight_grams || 0,
    wrap_canvas: !!p.wrap_canvas,
    shipping_format: p.shipping_format || "enveloppe",
    thickness_mm: p.thickness_mm || 0,
    images: p.images || [],
    material: p.material || "",
    dimensions: p.dimensions || "",
    production_time: p.production_time || "",
    care_instructions: p.care_instructions || "",
  });

  const saveDiscount = async () => {
    if (!dCode.trim()) { toast.error("Code requis"); return; }
    try {
      await api.post("/discounts", { code: dCode, percent: parseFloat(dPct), active: true });
      toast.success("Réduction créée");
      setDCode(""); setDPct(10); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteDiscount = async (code) => {
    await api.delete(`/discounts/${code}`);
    load();
  };

  const inputCls = "w-full border border-line bg-cream px-3 py-2 text-sm outline-none focus:border-ink";

  const shipOrder = async (id) => {
    try {
      const { data } = await api.post(`/orders/${id}/ship`);
      toast.success(data.already ? "Commande déjà expédiée" : "Expédiée — e-mail envoyé à l'acheteur");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleReview = async (id) => {
    try {
      const { data } = await api.post(`/reviews/${id}/approve`);
      toast.success(data.approved ? "Avis publié" : "Avis masqué");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    await api.delete(`/reviews/${id}`);
    toast.success("Avis supprimé");
    load();
  };

  const saveShipping = async () => {
    try {
      await api.put("/shipping/settings", ship);
      toast.success("Frais de port enregistrés");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO title="Administration" path="/admin" noindex />
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-line bg-cream/90 px-5 py-4 backdrop-blur-xl md:px-10">
        <div className="flex items-center gap-4">
          <Logo />
          <span className="hidden text-xs uppercase tracking-widest text-ink3 sm:block">· Administration</span>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="flex items-center gap-2 border border-line px-4 py-2 text-xs uppercase tracking-widest text-ink2 transition-colors hover:bg-sand">
          <LogOut className="h-4 w-4" /> Déconnexion
        </button>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 py-10 md:px-10">
        <h1 className="mb-8 font-serif text-4xl font-light tracking-tight text-ink md:text-5xl">Tableau de bord</h1>
        <Tabs defaultValue="products">
          <TabsList>
            <TabsTrigger value="products" data-testid="tab-products">Produits ({products.length})</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts">Réductions ({discounts.length})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Commandes ({orders.length})</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Avis ({reviews.filter((r) => !r.approved).length} à valider)</TabsTrigger>
            <TabsTrigger value="shipping" data-testid="tab-shipping">Livraison</TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account">Compte</TabsTrigger>
          </TabsList>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-6">
            <button onClick={() => setForm({ ...EMPTY })} data-testid="new-product-btn" className="mb-6 flex items-center gap-2 bg-ink px-5 py-3 text-xs uppercase tracking-widest text-cream transition-colors hover:bg-terra">
              <Plus className="h-4 w-4" /> Nouveau produit
            </button>

            {[
              { title: "Catalogue — objets à personnaliser", hint: "Visibles sur la page Catalogue", testid: "admin-section-catalogue",
                items: products.filter((p) => p.customizable) },
              { title: "Shop — pièces déjà réalisées", hint: "Visibles sur la page Shop", testid: "admin-section-shop",
                items: products.filter((p) => !p.customizable && p.in_realisations) },
              { title: "Non publiés", hint: "Ni personnalisables, ni dans le shop — invisibles sur le site", testid: "admin-section-unpublished",
                items: products.filter((p) => !p.customizable && !p.in_realisations) },
            ].map((sec) => (
              <section key={sec.testid} className="mb-10" data-testid={sec.testid}>
                <h3 className="mb-1 font-serif text-xl font-medium text-ink">{sec.title} <span className="text-sm text-ink3">({sec.items.length})</span></h3>
                <p className="mb-3 text-xs text-ink3">{sec.hint}</p>
                {sec.items.length === 0 ? (
                  <p className="border border-dashed border-line p-6 text-sm text-ink3">Aucun produit dans cette section.</p>
                ) : (
                  <ProductsTable items={sec.items} onEdit={editProduct} onDelete={deleteProduct} />
                )}
              </section>
            ))}
          </TabsContent>

          {/* DISCOUNTS */}
          <TabsContent value="discounts" className="mt-6">
            <div className="mb-6 flex flex-wrap items-end gap-3 border border-line bg-sand/40 p-5">
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Code</label>
                <input value={dCode} onChange={(e) => setDCode(e.target.value.toUpperCase())} data-testid="discount-code-input" placeholder="ETE2026" className={inputCls} />
              </div>
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Pourcentage</label>
                <input type="number" min="1" max="100" value={dPct} onChange={(e) => setDPct(e.target.value)} data-testid="discount-pct-input" className={`${inputCls} w-28`} />
              </div>
              <button onClick={saveDiscount} data-testid="save-discount-btn" className="bg-ink px-5 py-2 text-xs uppercase tracking-widest text-cream transition-colors hover:bg-terra">Créer / Mettre à jour</button>
            </div>
            <div className="overflow-hidden border border-line">
              <table className="w-full text-sm">
                <thead className="bg-sand text-left text-xs uppercase tracking-widest text-ink3">
                  <tr><th className="p-4">Code</th><th className="p-4">Réduction</th><th className="p-4">Statut</th><th className="p-4"></th></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {discounts.map((d) => (
                    <tr key={d.code} data-testid={`admin-discount-${d.code}`} className="hover:bg-sand/40">
                      <td className="p-4 font-medium text-ink">{d.code}</td>
                      <td className="p-4 text-ink2">−{d.percent}%</td>
                      <td className="p-4">{d.active ? <span className="text-sage">Actif</span> : <span className="text-ink3">Inactif</span>}</td>
                      <td className="p-4 text-right"><button onClick={() => deleteDiscount(d.code)} data-testid={`delete-discount-${d.code}`} className="p-2 text-ink2 hover:text-terra"><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                  {discounts.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-ink3">Aucune réduction.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-6">
            {orders.length === 0 && (
              <div className="border border-line p-10 text-center text-ink3">Aucune commande pour le moment.</div>
            )}
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-line bg-cream">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-sand/50 px-5 py-3">
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span className="font-medium text-ink">#{o.id.slice(0, 8)}</span>
                      <span className="text-ink3">{new Date(o.created_at).toLocaleString("fr-CH")}</span>
                      <span className="text-ink2">{o.customer_email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs uppercase tracking-wider ${o.payment_status === "paid" ? "bg-sage/15 text-sage" : o.payment_status === "pending" ? "bg-stonew text-ink3" : "bg-terra/15 text-terra"}`}>
                        {o.payment_status === "paid" ? "Payée" : o.payment_status === "pending" ? "En attente" : o.payment_status === "failed" ? "Échouée" : o.payment_status}
                      </span>
                      {o.payment_status === "paid" && (o.shipped ? (
                        <span data-testid={`shipped-badge-${o.id}`} className="flex items-center gap-1 bg-sage/15 px-2 py-1 text-xs uppercase tracking-wider text-sage">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Expédiée
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 bg-stonew px-2 py-1 text-xs uppercase tracking-wider text-ink3">
                          <Package className="h-3.5 w-3.5" /> À expédier
                        </span>
                      ))}
                      <span className="font-medium text-ink">{chf(o.total)}</span>
                    </div>
                  </div>

                  <div className="divide-y divide-line px-5">
                    {o.items?.map((it, idx) => {
                      const cust = it.customization || {};
                      const sides = cust.sides || null;
                      const comps = it.composites || [];
                      const thumb = comps.length ? fileUrl(comps[0].path) : imgSrc(it.image_url);
                      return (
                        <div key={idx} className="flex gap-4 py-4">
                          <img src={thumb} alt="" className="h-28 w-24 shrink-0 border border-line bg-white object-contain" />
                          <div className="flex-1 text-sm">
                            <p className="font-medium text-ink">{it.name} <span className="text-ink3">×{it.quantity}</span></p>
                            <p className="text-ink3">{it.category} · {chf(it.unit_price)}</p>
                            {it.size && <p className="mt-1 text-ink2">{it.size_label || "Taille"} : <strong>{it.size}</strong></p>}
                            {sides ? (
                              Object.entries(sides).map(([face, c]) => (
                                <div key={face} className="mt-1 border-l-2 border-terra/40 pl-2">
                                  <p className="text-[11px] uppercase tracking-wider text-terra">{face}</p>
                                  {c.text?.value && <p className="text-ink2">Texte : « {c.text.value} »</p>}
                                  {c.text?.font && <p className="text-ink2">Police : {fontLabel(c.text.font)}</p>}
                                  {c.text?.color && (
                                    <p className="flex items-center gap-1 text-ink2">Couleur :
                                      <span className="inline-block h-3 w-3 rounded-full border border-line align-middle" style={{ backgroundColor: c.text.color }} /> {c.text.color}
                                    </p>
                                  )}
                                  {c.image?.path && <a href={fileUrl(c.image.path)} target="_blank" rel="noreferrer" className="link-underline text-ink">Image du client ({face}) ↗</a>}
                                </div>
                              ))
                            ) : (cust.text || cust.image_path) ? (
                              <>
                                {cust.text && <p className="text-ink2">Texte : « {cust.text} »</p>}
                                {cust.image_path && <a href={fileUrl(cust.image_path)} target="_blank" rel="noreferrer" className="link-underline text-ink">Image du client ↗</a>}
                              </>
                            ) : null}
                            {comps.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-3 text-xs">
                                {comps.map((cp, k) => (
                                  <a key={k} href={fileUrl(cp.path)} target="_blank" rel="noreferrer" className="link-underline text-ink">Aperçu {cp.side} ↗</a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {o.payment_status === "paid" && !o.shipped && (
                    <div className="border-t border-line px-5 py-3 text-right">
                      <button
                        onClick={() => shipOrder(o.id)} data-testid={`ship-btn-${o.id}`}
                        className="inline-flex items-center gap-2 bg-ink px-5 py-2.5 text-xs uppercase tracking-widest text-cream transition-colors hover:bg-terra"
                      >
                        <Truck className="h-4 w-4" /> Marquer comme expédiée
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* REVIEWS */}
          <TabsContent value="reviews" className="mt-6">
            {reviews.length === 0 ? (
              <div className="border border-line p-10 text-center text-ink3">Aucun avis pour le moment.</div>
            ) : (
              <div className="overflow-x-auto border border-line">
                <table className="w-full text-sm">
                  <thead className="bg-sand text-left text-xs uppercase tracking-widest text-ink3">
                    <tr><th className="p-4">Date</th><th className="p-4">Produit</th><th className="p-4">Client</th><th className="p-4">Note</th><th className="p-4">Commentaire</th><th className="p-4">Statut</th><th className="p-4"></th></tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {reviews.map((r) => (
                      <tr key={r.id} data-testid={`admin-review-${r.id}`} className="hover:bg-sand/40">
                        <td className="p-4 text-ink3">{(r.created_at || "").slice(0, 10)}</td>
                        <td className="p-4 text-ink2">{products.find((p) => p.id === r.product_id)?.name || "—"}</td>
                        <td className="p-4 font-medium text-ink">{r.name}</td>
                        <td className="p-4"><Stars value={r.rating} size="h-3.5 w-3.5" /></td>
                        <td className="max-w-xs p-4 text-ink2"><span className="line-clamp-2">{r.comment || "—"}</span></td>
                        <td className="p-4">
                          {r.approved
                            ? <span className="flex items-center gap-1 text-sage"><CheckCircle2 className="h-3.5 w-3.5" /> Publié</span>
                            : <span className="text-terra">En attente</span>}
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => toggleReview(r.id)} data-testid={`approve-review-${r.id}`}
                              className={`border px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${r.approved ? "border-line text-ink2 hover:bg-sand" : "border-ink bg-ink text-cream hover:bg-terra"}`}>
                              {r.approved ? "Masquer" : "Publier"}
                            </button>
                            <button onClick={() => deleteReview(r.id)} data-testid={`delete-review-${r.id}`} className="p-2 text-ink2 hover:text-terra"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* SHIPPING */}
          <TabsContent value="shipping" className="mt-6">
            {!ship ? <Loader2 className="h-6 w-6 animate-spin text-terra" /> : (
              <div className="max-w-3xl space-y-8" data-testid="shipping-settings">
                <div className="border border-line bg-cream p-6">
                  <h3 className="font-serif text-xl font-medium text-ink">Emballage & seuils</h3>
                  <p className="mt-1 text-xs text-ink3">Le poids d'emballage s'ajoute au poids des articles. Le tarif colis s'applique si un article part en carton, si l'épaisseur totale dépasse le seuil, ou si le poids total dépasse la limite.</p>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Poids enveloppe (g)</label>
                      <input type="number" min="0" value={ship.packaging_enveloppe_g} onChange={(e) => setShip({ ...ship, packaging_enveloppe_g: e.target.value })} data-testid="ship-pack-enveloppe" className={inputCls} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Poids carton (g)</label>
                      <input type="number" min="0" value={ship.packaging_carton_g} onChange={(e) => setShip({ ...ship, packaging_carton_g: e.target.value })} data-testid="ship-pack-carton" className={inputCls} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Épaisseur max lettre (mm)</label>
                      <input type="number" min="0" value={ship.thickness_limit_mm} onChange={(e) => setShip({ ...ship, thickness_limit_mm: e.target.value })} data-testid="ship-thickness-limit" className={inputCls} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Poids max lettre (g) — au-delà → colis</label>
                      <input type="number" min="0" value={ship.colis_weight_limit_g} onChange={(e) => setShip({ ...ship, colis_weight_limit_g: e.target.value })} data-testid="ship-weight-limit" className={inputCls} />
                    </div>
                  </div>
                </div>

                {["lettre", "colis"].map((grid) => (
                  <div key={grid} className="border border-line bg-cream p-6" data-testid={`ship-grid-${grid}`}>
                    <h3 className="font-serif text-xl font-medium text-ink">Grille {grid === "lettre" ? "LETTRE" : "COLIS"}</h3>
                    <div className="mt-4 space-y-2">
                      {(ship[grid] || []).map((t, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-sm text-ink3">≤</span>
                          <input type="number" min="1" value={t.max_g} data-testid={`ship-${grid}-max-${i}`}
                            onChange={(e) => setShip({ ...ship, [grid]: ship[grid].map((x, j) => j === i ? { ...x, max_g: e.target.value } : x) })}
                            className={`${inputCls} w-32`} />
                          <span className="text-sm text-ink3">g →</span>
                          <input type="number" min="0" step="0.10" value={t.price} data-testid={`ship-${grid}-price-${i}`}
                            onChange={(e) => setShip({ ...ship, [grid]: ship[grid].map((x, j) => j === i ? { ...x, price: e.target.value } : x) })}
                            className={`${inputCls} w-28`} />
                          <span className="text-sm text-ink3">CHF</span>
                          <button onClick={() => setShip({ ...ship, [grid]: ship[grid].filter((_, j) => j !== i) })}
                            data-testid={`ship-${grid}-remove-${i}`} className="p-2 text-ink2 hover:text-terra" aria-label="Supprimer ce palier">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => setShip({ ...ship, [grid]: [...(ship[grid] || []), { max_g: 1000, price: 0 }] })}
                        data-testid={`ship-${grid}-add`}
                        className="mt-2 border border-line px-3 py-1.5 text-xs uppercase tracking-wider text-ink2 transition-colors hover:bg-sand">
                        + Ajouter un palier
                      </button>
                    </div>
                  </div>
                ))}

                <button onClick={saveShipping} data-testid="ship-save"
                  className="bg-ink px-8 py-3 text-xs uppercase tracking-widest text-cream transition-colors hover:bg-terra">
                  Enregistrer les frais de port
                </button>
              </div>
            )}
          </TabsContent>

          {/* ACCOUNT */}
          <TabsContent value="account" className="mt-6">
            <div className="max-w-md border border-line bg-cream p-8" data-testid="account-panel">
              <h2 className="mb-1 font-serif text-2xl font-medium text-ink">Changer le mot de passe</h2>
              <p className="mb-6 text-sm text-ink3">Connecté en tant que {user?.email}</p>
              <div className="space-y-3">
                <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" placeholder="Mot de passe actuel" className={inputCls} />
                <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-next" placeholder="Nouveau mot de passe (min. 8 caractères)" className={inputCls} />
                <input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" placeholder="Confirmer le nouveau mot de passe" className={inputCls} />
                <button onClick={changePassword} data-testid="pw-submit" className="w-full bg-ink py-3 text-sm uppercase tracking-widest text-cream transition-colors hover:bg-terra">
                  Mettre à jour
                </button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* PRODUCT FORM DRAWER */}
      {form && (
        <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={() => setForm(null)}>
          <div className="flex h-full w-full max-w-md flex-col bg-cream shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="product-form">
            <div className="flex items-center justify-between border-b border-line px-8 py-5">
              <h2 className="font-serif text-2xl font-medium text-ink">{form.id ? "Modifier" : "Nouveau"} produit</h2>
              <button onClick={() => setForm(null)} data-testid="close-product-form-btn" className="p-2 text-ink2 hover:text-ink"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-8 py-6" data-lenis-prevent>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Nom</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" className={inputCls} />
              </div>
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Catégorie</label>
                <select value={form.category} onChange={(e) => { const c = e.target.value; setForm({ ...form, category: c, size_label: SIZE_LABEL_BY_CAT[c] || form.size_label || "Taille" }); }} data-testid="form-category" className={inputCls}>
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Technique</label>
                <select value={form.technique} onChange={(e) => setForm({ ...form, technique: e.target.value })} data-testid="form-technique" className={inputCls}>
                  <option value="Sublimation">Sublimation</option>
                  <option value="Gravure laser">Gravure laser</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="form-description" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Matière / support (facultatif)</label>
                  <input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} data-testid="form-material" placeholder="ex : Céramique émaillée" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Dimensions (facultatif)</label>
                  <input value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} data-testid="form-dimensions" placeholder="ex : Ø 8 cm × H 9,5 cm" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Délai de fabrication (facultatif)</label>
                  <input value={form.production_time} onChange={(e) => setForm({ ...form, production_time: e.target.value })} data-testid="form-production-time" placeholder="ex : 2 à 4 jours ouvrés" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Entretien (facultatif)</label>
                  <input value={form.care_instructions} onChange={(e) => setForm({ ...form, care_instructions: e.target.value })} data-testid="form-care" placeholder="ex : Lavage à la main conseillé" className={inputCls} />
                </div>
              </div>
              <p className="-mt-2 text-xs text-ink3">Ces détails s'affichent sur la fiche produit sous le bouton « Ajouter au panier » (uniquement les champs remplis).</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Prix (CHF)</label>
                  <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Réduction (%)</label>
                  <input type="number" min="0" max="100" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} data-testid="form-discount" className={inputCls} />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Poids (grammes) — pour les frais de port</label>
                <input type="number" min="0" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} data-testid="form-weight" placeholder="ex : 350" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Format d'envoi</label>
                  <select value={form.shipping_format} onChange={(e) => setForm({ ...form, shipping_format: e.target.value })} data-testid="form-shipping-format" className={inputCls}>
                    <option value="enveloppe">Enveloppe</option>
                    <option value="carton">Carton</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Épaisseur (mm)</label>
                  <input type="number" min="0" value={form.thickness_mm} onChange={(e) => setForm({ ...form, thickness_mm: e.target.value })} data-testid="form-thickness" placeholder="ex : 15" className={inputCls} />
                </div>
              </div>
              <p className="-mt-2 text-xs text-ink3">Un seul article « carton », une épaisseur totale &gt; 20 mm ou un poids total &gt; 1 kg → tarif colis. Sinon tarif lettre (voir onglet Livraison).</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Intitulé des tailles</label>
                  <input value={form.size_label} onChange={(e) => setForm({ ...form, size_label: e.target.value })} data-testid="form-size-label" placeholder="Taille / Contenance" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Tailles (facultatif)</label>
                  <input value={form.sizes_input} onChange={(e) => setForm({ ...form, sizes_input: e.target.value })} data-testid="form-sizes" placeholder="300 ml, 450 ml  ·  20x30 cm" className={inputCls} />
                </div>
              </div>
              <p className="-mt-2 text-xs text-ink3">Séparez par des virgules. Contenants : « 300 ml, 450 ml ». Planches/supports : « 20x30 cm, 30x40 cm ». Laissez vide si sans variante.</p>
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Image principale</label>
                {form.image_url && <img src={form.image_url} alt="" className="mb-2 h-32 w-full border border-line object-cover" />}
                <label className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-line py-3 text-sm text-ink2 hover:bg-sand">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Téléverser une image
                  <input type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="form-image-upload" />
                </label>
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="ou collez une URL" className={`${inputCls} mt-2`} />
              </div>
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-ink3">Images supplémentaires (facultatif)</label>
                {(form.images || []).length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2" data-testid="form-extra-images">
                    {form.images.map((u, idx) => (
                      <div key={idx} className="relative">
                        <img src={u} alt="" className="h-20 w-20 border border-line object-cover" />
                        <button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, i) => i !== idx) })}
                          data-testid={`remove-extra-image-${idx}`} aria-label="Retirer cette image"
                          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-cream hover:bg-terra">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-line py-3 text-sm text-ink2 hover:bg-sand">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Ajouter une image
                  <input type="file" accept="image/*" onChange={uploadExtraImage} className="hidden" data-testid="form-extra-image-upload" />
                </label>
                <div className="mt-2 flex gap-2">
                  <input value={form.extra_image_url || ""} onChange={(e) => setForm({ ...form, extra_image_url: e.target.value })}
                    placeholder="ou collez une URL" className={inputCls} data-testid="form-extra-image-url" />
                  <button type="button" data-testid="form-extra-image-add"
                    onClick={() => { const u = (form.extra_image_url || "").trim(); if (!u) return; setForm({ ...form, images: [...(form.images || []), u], extra_image_url: "" }); }}
                    className="shrink-0 border border-ink px-3 text-xs uppercase tracking-wider text-ink transition-colors hover:bg-ink hover:text-cream">
                    Ajouter
                  </button>
                </div>
                <p className="mt-1 text-xs text-ink3">Elles apparaissent en vignettes sur la fiche produit (cliquables pour agrandir).</p>
              </div>
              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-ink2">
                  <input type="checkbox" checked={form.customizable} onChange={(e) => setForm({ ...form, customizable: e.target.checked })} data-testid="form-customizable" /> Personnalisable → publié dans le <strong>Catalogue</strong>
                </label>
                <label className="flex items-center gap-2 text-sm text-ink2">
                  <input type="checkbox" checked={!!form.wrap_canvas} onChange={(e) => setForm({ ...form, wrap_canvas: e.target.checked })} data-testid="form-wrap-canvas" /> Canevas blanc « vue déroulée »
                </label>
                <label className="flex items-center gap-2 text-sm text-ink2">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="form-active" /> Actif
                </label>
                <label className="flex items-center gap-2 text-sm text-ink2">
                  <input type="checkbox" checked={form.in_realisations} onChange={(e) => setForm({ ...form, in_realisations: e.target.checked })} data-testid="form-in-realisations" /> Pièce déjà réalisée → publiée dans le <strong>Shop</strong>
                </label>
                <label className="flex items-center gap-2 text-sm text-ink2">
                  <input type="checkbox" checked={form.featured_home} onChange={(e) => setForm({ ...form, featured_home: e.target.checked })} data-testid="form-featured-home" /> À la une (accueil)
                </label>
              </div>
              <p className="-mt-2 text-xs text-ink3">Canevas blanc : pour les objets ronds (mugs, gourdes, tirelires…) — la personnalisation se fait sur une surface blanche à plat au lieu de la photo, pour un placement précis du motif.</p>
            </div>
            </div>
            <div className="border-t border-line bg-cream p-4">
              <button onClick={saveProduct} data-testid="save-product-btn" className="w-full bg-ink py-3 text-sm uppercase tracking-widest text-cream transition-colors hover:bg-terra">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
